import * as React from 'react'
import { WorkflowBlockIcon } from '~/components/icons/workflow'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { useGetTabPrefix, useTabsStore } from '../../tabs/store'
import { useRepoStore } from '../../repo-store'
import { useActiveBlockGenerations } from '~/hooks-data/workflow/use-active-block-generations'
import {
  Block,
  WorkflowBlock,
  WorkflowRun,
  isWorkflowBlock,
} from '@basehub/data/replicache/index'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { ClockIcon } from '@radix-ui/react-icons'
import { Spinner } from '~/components/misc/spinner'
import { useTreeSlice } from '~/hooks/use-tree-slice'
import { AlertIcon } from '~/sotano-kit/icons/alert'
import { useEditorMode } from '../../editor-mode-provider'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { useSearchParams } from 'next/navigation'
import { WORKFLOW_AUTOFILL_TIME_MS } from '@basehub/shared-constants'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useUserStore } from '~/app/_store/user-store'

export const WorkflowRunControls = ({
  sourceBlockId,
  instanceChildTarget,
}: {
  sourceBlockId: string
  instanceChildTarget: Block['instanceChildTarget']
}) => {
  const tree = useWorkingTreeStore((s) => s.tree)
  const generatedBy = useTreeSlice(tree, (s) => {
    const index = s.compoundIndexes.get('blocksByType') as Map<
      Block['type'],
      Set<string>
    >
    if (!index) return false
    const workflows = index.get('workflow')
    if (!workflows) return false

    const isAiGeneratedBy = Array.from(workflows).find((id) => {
      const workflow = s.getNode(id)?.value
      if (!workflow) return
      if (!isWorkflowBlock(workflow)) return

      const blockIsGenerated = Object.values(workflow.value?.actions).some(
        (a) =>
          a.type === 'ai-autofill' &&
          (a.blockId === sourceBlockId ||
            (Boolean(instanceChildTarget) && a.blockId === instanceChildTarget))
      )

      return blockIsGenerated
    })

    return isAiGeneratedBy
  })

  if (!generatedBy) return null

  return (
    <AutofilledBlockBadges blockId={sourceBlockId} generatedBy={generatedBy} />
  )
}

export const AutofilledBlockBadges = ({
  blockId,
  generatedBy,
}: {
  blockId: string
  generatedBy: WorkflowBlock['id']
}) => {
  const { running, pending, failed } = useActiveBlockGenerations(blockId)
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const encodedSegment = useBranchOrCommitSegment().encodedSegment
  const addTab = useTabsStore((s) => s.addTab)
  const searchParams = useSearchParams()
  const setFocus = useRepoStore((s) => s.setFocus)
  const workflowNode = useWorkingTreeStore((s) => {
    if (!s.tree) return null
    return s.tree.getNode(generatedBy)
  })

  const editorMode = useEditorMode()
  const hasEditWrights = editorMode !== 'read' && editorMode !== 'preview'

  if (running.length && hasEditWrights) {
    return (
      <RunningBadge
        running={running as [WorkflowRun, ...WorkflowRun[]]}
        blockId={blockId}
      />
    )
  }

  if (pending.length && hasEditWrights) {
    return (
      <ScheduledBadge
        pending={pending as [WorkflowRun, ...WorkflowRun[]]}
        blockId={blockId}
      />
    )
  }

  if (failed && hasEditWrights) {
    return <FailedBadge run={failed} blockId={blockId} />
  }

  if (!workflowNode || !workflowNode.parentKey) return null

  return (
    <SimpleTooltip content="Workflow affecting this block" asChild>
      <ButtonLink
        unstyled
        focusStyled
        href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${workflowNode.parentKey}${searchParams.toString()}#${workflowNode.value.id}`}
        onClick={() => {
          if (!workflowNode.parentKey) return
          addTab({ key: workflowNode.parentKey, partiallyOpen: true })
          setFocus({
            blockId: workflowNode.value.id,
            type: 'block:wrapper',
          })
        }}
        className="rounded bg-dark-border-solid text-dark-muted size-5 flex items-center justify-center hover:text-dark-dim cursor-default"
      >
        <WorkflowBlockIcon />
      </ButtonLink>
    </SimpleTooltip>
  )
}

const RunningBadge = ({
  running,
  blockId,
}: {
  running: [WorkflowRun, ...WorkflowRun[]]
  blockId: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const setFocus = useRepoStore((s) => s.setFocus)
  const tabPrefix = useGetTabPrefix()
  const rootBlockId = useWorkingTreeStore((s) => s.tree?.root.value.id)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const activeDocument = activeTabKey
    ? getNode?.(activeTabKey, { fallbackKey: 'sourceBlockId' })?.value
    : null
  const addTab = useTabsStore((s) => s.addTab)
  const [firstRun] = running
  const workflowBlockParentId = React.useMemo(() => {
    if (!getNode) return undefined
    const workflowBlock = getNode(firstRun.workflowBlockId)
    if (!workflowBlock || !workflowBlock.parentKey) return null
    return idToSourceBlockId(workflowBlock.parentKey, { getNode })
  }, [firstRun.workflowBlockId, getNode])
  const workflowSourceBlockId = getNode
    ? idToSourceBlockId(firstRun.workflowBlockId, {
        getNode,
      })
    : undefined

  return (
    <span className="rounded-md h-5 bg-dark-control-base/15 pl-1 border border-dark-control-base/15 text-dark-control-base text-xs flex items-center font-medium">
      <ButtonLink
        unstyled
        href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${workflowBlockParentId}#${workflowSourceBlockId}`}
        className="pr-1.5 flex items-center hover:underline whitespace-nowrap"
        onClick={() => {
          if (!workflowSourceBlockId) return

          setFocus({
            type: 'block:wrapper',
            blockId: workflowSourceBlockId,
            temporaryBackLink: activeDocument
              ? {
                  title: activeDocument?.title || 'Untitled',
                  id: idToSourceBlockId(activeDocument),
                  edgeId: blockId,
                }
              : undefined,
          })
          addTab({
            key: `${tabPrefix}${workflowBlockParentId}`,
            partiallyOpen: true,
          })
        }}
      >
        <Spinner className="size-3 mr-1" />
        Workflow running
      </ButtonLink>
      <Button
        unstyled
        className="border-l rounded-r-md -mr-px h-5 bg-dark-control-base/15 border-dark-control-base/15 hover:bg-dark-control-base/30 px-1.5 cursor-default"
        onClick={async () => {
          if (!replicache || !rootBlockId) return
          const runningIds = running.map((r) => r.id)
          await replicache.mutate.stopWorkflowRuns({
            ids: runningIds,
            isoNow: new Date().toISOString(),
            username: optimisticUser?.username,
            rootBlockId,
          })
        }}
      >
        Stop
      </Button>
    </span>
  )
}

const ScheduledBadge = ({
  pending,
  blockId,
}: {
  pending: [WorkflowRun, ...WorkflowRun[]]
  blockId: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const setFocus = useRepoStore((s) => s.setFocus)
  const tabPrefix = useGetTabPrefix()
  const rootBlockId = useWorkingTreeStore((s) => s.tree?.root.value.id)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const activeDocument = activeTabKey
    ? getNode?.(activeTabKey, { fallbackKey: 'sourceBlockId' })?.value
    : null
  const addTab = useTabsStore((s) => s.addTab)
  const [firstRun] = pending
  const workflowBlockParentId = React.useMemo(() => {
    if (!getNode) return undefined
    const workflowBlock = getNode(firstRun.workflowBlockId)
    if (!workflowBlock || !workflowBlock.parentKey) return null
    return idToSourceBlockId(workflowBlock.parentKey, { getNode })
  }, [firstRun.workflowBlockId, getNode])
  const workflowSourceBlockId = getNode
    ? idToSourceBlockId(firstRun.workflowBlockId, {
        getNode,
      })
    : undefined
  const dateToConsiderPlus5Min = new Date(
    firstRun.updatedAt ?? firstRun.createdAt
  )
  dateToConsiderPlus5Min.setTime(
    dateToConsiderPlus5Min.getTime() + WORKFLOW_AUTOFILL_TIME_MS
  )
  const timeDiff = useTimeDiff(dateToConsiderPlus5Min, {
    refreshIntervalMs: 15000,
  })

  return (
    <span className="rounded-md h-5 bg-dark-shade pl-1 border border-dark-border-solid text-dark-muted text-xs flex items-center font-medium">
      <ButtonLink
        unstyled
        href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${workflowBlockParentId}#${workflowSourceBlockId}`}
        className="pr-1.5 flex items-center hover:underline whitespace-nowrap"
        onClick={() => {
          if (!workflowSourceBlockId) return
          setFocus({
            type: 'block:wrapper',
            blockId: workflowSourceBlockId,
            temporaryBackLink: activeDocument
              ? {
                  title: activeDocument?.title || 'Untitled',
                  id: idToSourceBlockId(activeDocument),
                  edgeId: blockId,
                }
              : undefined,
          })
          addTab({
            key: `${tabPrefix}${workflowBlockParentId}`,
            partiallyOpen: true,
          })
        }}
      >
        <ClockIcon className="size-4 mr-1" />
        Workflow runs {timeDiff}
      </ButtonLink>
      <Button
        unstyled
        className="border-l rounded-r-md -mr-px h-5 border-dark-border-solid hover:bg-dark-shade-hover/70 px-1.5 cursor-default"
        onClick={async () => {
          const branchId = pending[0]?.branchId
          if (!branchId || !rootBlockId) return
          await replicache?.mutate.runWorkflows({
            ids: pending.map((run) => run.id),
            branchId,
            isoNow: new Date().toISOString(),
            metadata: {},
            rootBlockId,
          })
        }}
      >
        Run now
      </Button>
    </span>
  )
}

const FailedBadge = ({
  run,
  blockId,
}: {
  run: WorkflowRun
  blockId: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const setFocus = useRepoStore((s) => s.setFocus)
  const tabPrefix = useGetTabPrefix()
  const rootBlockId = useWorkingTreeStore((s) => s.tree?.root.value.id)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const activeDocument = activeTabKey
    ? getNode?.(activeTabKey, { fallbackKey: 'sourceBlockId' })?.value
    : null
  const addTab = useTabsStore((s) => s.addTab)
  const workflowBlockParentId = React.useMemo(() => {
    if (!getNode) return undefined
    const workflowBlock = getNode(run.workflowBlockId)
    if (!workflowBlock || !workflowBlock.parentKey) return null
    return idToSourceBlockId(workflowBlock.parentKey, { getNode })
  }, [run.workflowBlockId, getNode])
  const workflowSourceBlockId = getNode
    ? idToSourceBlockId(run.workflowBlockId, {
        getNode,
      })
    : undefined

  return (
    <span className="rounded-md h-5 bg-dark-error/10 pl-1 border border-dark-error/10 text-dark-error text-xs flex items-center font-medium">
      <ButtonLink
        unstyled
        href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${workflowBlockParentId}#${workflowSourceBlockId}`}
        className="pr-1.5 flex items-center hover:underline whitespace-nowrap"
        onClick={() => {
          if (!workflowSourceBlockId) return

          setFocus({
            type: 'block:wrapper',
            blockId: workflowSourceBlockId,
            temporaryBackLink: activeDocument
              ? {
                  title: activeDocument?.title || 'Untitled',
                  id: idToSourceBlockId(activeDocument),
                  edgeId: blockId,
                }
              : undefined,
          })
          addTab({
            key: `${tabPrefix}${workflowBlockParentId}`,
            partiallyOpen: true,
          })
        }}
      >
        <AlertIcon className="size-4 mr-1" />
        Generation failed
      </ButtonLink>
      <Button
        unstyled
        className="border-l rounded-r-md -mr-px h-5 border-dark-error/10 bg-dark-error/10 hover:bg-dark-error/20 px-1.5 cursor-default"
        onClick={async () => {
          if (!replicache || !rootBlockId) return
          await replicache.mutate.runWorkflows({
            ids: [run.id],
            branchId: run.branchId,
            isoNow: new Date().toISOString(),
            metadata: {},
            rootBlockId,
          })
        }}
      >
        Retry
      </Button>
    </span>
  )
}
